<template>
  <div class="mgt-indicators f-h">
    <FiltersZone>
      <div slot="shortcuts" class="v-centerer">
        <ChartPeriodSelector
          ref="periodSelector"
          v-model="displayConfig"
          :preset-id="presetId"
          query-prefix="mgtIndicators"
          :hide-preset-field="$store.state.device.isSmartphonePortrait"
          show-default-wheel-view
          @presetPick="presetId = $event"
        />
        <div class="mgt-indicators__view">
          <span class="legend">{{ $t("message.VISUAL_MODE") + $t("message.TWO_DOTS") }}</span>
          <Switcher
            v-model="mgtIdcOptViewMode"
            :titles="viewModes"
            style="margin-left:5px;"
          />
        </div>
        <!-- Cumulated mode: Pick a scheme -->
        <EditableLabelRadio
          v-if="mgtIdcOptViewMode == 1"
          v-model="schemeId"
          class="mgt-indicators__templates"
          :items="allSchemes"
          label-key="name"
          :legend="$t('message.TEMPLATE') + $t('message.TWO_DOTS')"
          ellipsize
        />
      </div>
      <h2 class="basic-sub-title">
        {{ $t('message.OPTIONS') }}
      </h2>
      <ComponentsList :value="mgtIdcOptions" @edit="paramsEdit" />
    </FiltersZone>
    <div class="f-h-below-filters">
      <!-- COMPACT MODE -->
      <div v-if="mgtIdcOptViewMode == 0">
        <ManagementIndicatorsCompactRow
          v-for="team in teamsData"
          :key="`${team.id}`"
          :team="team"
          :selected="team.id === selectedCompactTeamId"
          :custom-config="displayConfig"
          :range="allValuesRange"
          :visible-chart-range="visibleValuesRange"
          :display-gw-indicators="mgtIdcDisplayGw"
          @click.native="selectedCompactTeamId = team.id"
        />
      </div>
      <!-- CUMULATED MODE -->
      <Indicators
        v-else
        :mgt-themes="cumulatedThemes"
        :mgt-display-config="displayConfig"
        :mgt-working-days="accuWorkingDays"
        :mgt-team-ids="selectedTeamIds"
        hide-header
        @chartClicked="detailedIndicId = $event.id"
      />
      <!-- Overlay to see the indicators of a specific team during the given period -->
      <transition name="fade">
        <Indicators
          v-if="selectedCompactTeam !== undefined"
          ref="indicatorsBoard"
          :team="selectedCompactTeam"
          :mgt-display-config="displayConfig"
          :mgt-hidden-indicators="compactHiddenIndicators"
          as-component
          hide-header
          @closeClick="selectedCompactTeamId = null"
        />
      </transition>
      <transition name="fade">
        <div v-if="detailedIndicDisplayed" class="mgt-indic-details f-h f-w nw">
          <CloseButton style="margin-left:10px" @click.native="detailedIndicId = undefined" />
          <h2>{{ detailedIndicName }}</h2>
          <div class="below-h2">
            <DetailedIndicator
              ref="indicatorDetailed"
              :working-days="accuWorkingDays"
              :bound-config="displayConfig"
              :team-ids="selectedTeamIds"
              as-component
              analysis
              hide-pb-actions
              cumulated
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
/**
 * Management page for problems synthesis
 */

import { cloneDeep, isEqual, omit } from 'lodash';
import $ from 'jquery';
import { mapState } from 'vuex';
// Components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import CloseButton from '@/components/close-button.vue';
import ComponentsList from '@/components/components-list.vue';
import DetailedIndicator from '@/views/overlay/detailed-indicator.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Indicator from '@/components/indicator.vue';
import Indicators from '@/views/dmp/indicators.vue';
import LeftDrawerPanel from '@/components/left-drawer-panel.vue';
import ManagementIndicatorsCompactRow from '@/components/management-indicators-compact-row.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Switcher from '@/components/switcher.vue';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
import mixinMgtPagesTeams from '@/mixins/mgt-pages-teams.vue';
import mixinMgtIdcOptions from '@/mixins/page-options/mgt-indicators.vue';
// Vuex
import { FETCH_SCHEMES } from '@/store/modules/config/action-types';
import { GET_SCHEMES } from '@/store/modules/config/mutation-types';
import {
  FETCH_CUMULATED_INDICATORS,
  FETCH_MANAGED_INDICATORS,
  FETCH_INDICATOR_DISPLAY_CONFIGS,
} from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Utils
import { computeConfigStart } from '@/utils/indicator-utils';
import {
  decodeHTML,
  calcPeriodStartDate,
} from '@/utils/main';


export default {

  name: 'MgtIndicators',

  components: {
    ChartPeriodSelector,
    CloseButton,
    ComponentsList,
    DetailedIndicator,
    EditableLabelRadio,
    Indicator,
    Indicators,
    LeftDrawerPanel,
    ManagementIndicatorsCompactRow,
    FiltersZone,
    Switcher,
  },

  mixins: [mixinCommonColors, mixinMgtPagesTeams, mixinMgtIdcOptions],

  data() {
    return {
      displayConfig: this.$store.getters.defaultIndicDisplayConfig,
      // Cumulated indicator data per team
      teamsData: [],
      cumulationTeamsData: [],
      detailedIndicDisplayed: false,
      detailedIndicName: '',
      lastSelectedLibraryIds: [],
      /** View */
      viewModes: [this.$t('message.COMPACT'), this.$t('message.CUMUL')],
      /** Scheme choice when vueMode is 1 */
      lastSchemeId: 0,
      accuThemes: [],
      mqlSmallestHeight: window.matchMedia('(max-height: 450px)'),
      mqlSmallHeight: window.matchMedia('(max-height: 550px)'),
      mqlMediumHeight: window.matchMedia('(max-height: 700px)'),
      refreshTimeout: null,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.MGT_INDICATORS'),
    };
  },

  computed: {
    /** Pickable schemes to display a pre-defined set of indicators */
    allSchemes() {
      return [
        { id: 0, name: this.$t('message.NONE') },
        ...this.$store.state.config[GET_SCHEMES.stateKey] || [],
      ];
    },
    /** Array: Computed themes displayed in the cumulated view. Sorted if any scheme selected */
    cumulatedThemes() {
      const scheme = cloneDeep(this.allSchemes.find(item => item.id === this.lastSchemeId));
      if (!scheme || !scheme.themes) {
        return this.accuThemes;
      }
      // Sort themes and indicators by scheme order
      const iOrder = scheme.themes.flatMap(t => t.indicators.sort((a, b) => a.orderKey - b.orderKey).map(i => i.libraryId));
      this.accuThemes.forEach((theme) => {
        theme.indicators.sort((a, b) => iOrder.indexOf(a.id) - iOrder.indexOf(b.id));
      });
      return this.accuThemes.sort((a, b) => (a.indicators.length && b.indicators.length
        ? iOrder.indexOf(a.indicators[0].id) - iOrder.indexOf(b.indicators[0].id)
        : a.indicators.length - b.indicators.length));
    },
    /** Return working days in current scheme */
    accuWorkingDays() {
      return [false, false, false, false, false, false, false];
      const wd = (this.mgtIdcOptViewMode === 1 ? this.cumulationTeamsData : this.teamsData)
        .reduce((acc, team) => acc.concat(team.workingDays), [])
        .filter((val, i, wds) => wds.indexOf(val) === i);
      const out = [false, false, false, false, false, false, false];
      wd.forEach((val) => { out[val] = true; });
      return out;
    },
    range() {
      return this.visibleValuesRange;
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj).startOf('day'),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /** Object: Range of all values, including values not displayed. See dmp/indicators */
    allValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      if (!this.displayConfig.increasingValues) {
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart).startOf('day'), end: chartEnd };
    },
    /** Object: Team object selected in compact view (or null) */
    selectedCompactTeam() {
      return this.teamsData.find(t => t.id === this.selectedCompactTeamId);
    },
    /** Array: Default presets merged with cutom presets */
    allPresets() {
      return [
        ...this.$store.getters.indicDisplayConfigs,
        ...this.customConfigs.map(item => ({
          ...item,
          pointPeriodObj: { period: item.pointPeriod, unit: item.pointUnit, startDay: item.pointStartDay },
          chartPeriodObj: { period: item.chartPeriod, unit: item.chartUnit },
        })),
      ];
    },
    /** Array: Indicator ids to hide in the selected team's indicators overlay */
    compactHiddenIndicators() {
      if (this.mgtIdcDisplayGw) {
        return [];
      }
      return this.selectedCompactTeam.themes.reduce(
        (all, theme) => [...all, ...theme.indicators.filter(indic => indic.isGw === 1).map(({ id }) => id)],
        [],
      );
    },
    // Custom configs
    ...mapState({
      customConfigs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      customConfigsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),
    /** Array: Array of the selected teams, defaults to teams managed by current team */
    mgtPagesOptTeams() {
      return this.selectedTeamIds;
    },
    /** Number: ID of the default preset for indicator display */
    mgtIdcOptDefaultConfig: {
      get() {
        return this.presetId;
      },
      set(presetId) {
        this.presetId = presetId;
        this.displayConfig = this.allPresets.find(preset => preset.id === presetId);
      },
    },
    /**
     * Query params
     */
    /** Number: two views can be displayed: cumulated (1) and compact (0) */
    mgtIdcOptViewMode: {
      get() {
        return this.$route.query.viewMode === undefined ? 1 : Number(this.$route.query.viewMode) % 2;
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (Number(query.viewMode) !== newVal) {
          query.viewMode = newVal;
          this.$router.replace({ query });
        }
      },
    },
    /** Boolean: Wether GW indicators should be displayed or not */
    mgtIdcDisplayGw: {
      get() {
        return this.$route.query.mgtIdcDisplayGw === 'true';
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.mgtIdcDisplayGw = newVal ? 'true' : 'false';
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** A scheme is a set of library indicators to be defined in /config */
    schemeId: {
      get() {
        const schemeId = Number(this.$route.query.schemeId || 0);
        this.lastSchemeId = schemeId === 0 ? this.lastSchemeId : schemeId;
        return schemeId;
      },
      set(newVal) {
        const scheme = this.allSchemes.find(item => item.id === newVal);
        const ids = scheme.themes.flatMap(t => t.indicators.map(i => i.libraryId));
        const query = Object.assign({}, this.$route.query);
        if (Number(query.schemeId) !== newVal || ids !== query.mgtIdcOptLibraryIndicators) {
          query.schemeId = this.lastSchemeId = newVal;
          query.mgtIdcOptLibraryIndicators = ids.join(',');
          this.$router.replace({ query });
        }
      },
    },
    /** Array: Selected library indicators ids */
    mgtIdcOptSelectedLibraryIndicators() {
      const { mgtIdcOptLibraryIndicators } = this.$route.query;
      if (mgtIdcOptLibraryIndicators) {
        return cloneDeep(mgtIdcOptLibraryIndicators.split(',').map(id => Number(id)));
      }
      return [];
    },
    /** Indicator display config ID */
    presetId: {
      get() {
        const { presetId, mgtIdcOptDefaultConfig: defaultPresetId } = this.$route.query;
        if (presetId === null || isNaN(presetId)) {
          return defaultPresetId === null || isNaN(defaultPresetId) ? 1 : Number(defaultPresetId);
        }
        return Number(presetId);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (newVal !== Number(query.presetId)) {
          query.presetId = newVal;
          this.$router.replace({ query });
        }
      },
    },
    /** Number: ID of the team selected in the compact view (or null) */
    selectedCompactTeamId: {
      get() {
        const { mgtIndicsCompactTeam } = this.$route.query;
        return Number(mgtIndicsCompactTeam) || null;
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (!newVal) {
          delete query.mgtIndicsCompactTeam;
        } else {
          query.mgtIndicsCompactTeam = newVal;
        }
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Number: ID of the team selected in the compact view (or null) */
    detailedIndicId: {
      get() {
        return Number(this.$route.query.detailed || 0);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.detailed = newVal;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    // Properties used by other components. See src/views/readme.md
    pageBackButtonAction() {
      if (this.selectedCompactTeam) {
        return () => {
          this.selectedCompactTeamId = null;
        };
      }
      return false;
    },
  },

  watch: {
    schemeId() {
      this.refresh();
    },
    mgtIdcOptSelectedLibraryIndicators(newVal) {
      // NOTE: Using a watcher only on the computed property simply not works : newVal and oldVal are the same
      if (!isEqual(newVal, this.lastSelectedLibraryIds)) {
        this.lastSelectedLibraryIds = cloneDeep(newVal);
        this.refresh();
      }
    },
    range(newVal, oldVal) {
      if (!newVal.start.isSame(oldVal.start) || !newVal.end.isSame(oldVal.end)) {
        this.refresh();
      }
    },
    displayConfig(newValue, oldValue) {
      if (!isEqual(omit(newValue, 'chartEnd'), omit(oldValue, 'chartEnd'))) {
        this.refresh();
      }
    },
    detailedIndicId() {
      this.setupDetailedIndic();
    },
    mgtPagesOptTeams() {
      // Refresh the static content of Detailed indicator
      if (this.detailedIndicId) {
        this.setupDetailedIndic();
      }
    },
    mgtIdcOptViewMode() {
      this.detailedIndicId = undefined;
      this.refresh();
    },
  },

  created() {
    this.$store.dispatch(`config/${FETCH_SCHEMES}`, { onlyManagedTeams: true }).then(() => {
      const schemes = this.$store.state.config[GET_SCHEMES.stateKey] || [];
      if (schemes.length && this.mgtIdcOptSelectedLibraryIndicators.length === 0) {
        const defaultScheme = schemes.find(scheme => scheme.isDefault == 1);
        this.schemeId = defaultScheme ? defaultScheme.id : schemes[0].id;
      }
    });
    // Fetch any custom configs
    if (!this.customConfigsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }

    this.refresh();
    $(window).on(`resizeEnd.${this.uuid}`, () => { this.redrawAll(); });
  },

  methods: {
    decodeHTML,
    /** Set a timeout to refresh the indicator values */
    refresh() {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(this.effectiveRefresh, 50);
    },
    /** Effectively call the refresh XHR */
    effectiveRefresh() {
      if (!this.selectedTeamIds || !this.selectedTeamIds.length) {
        return;
      }
      const { start, end } = this.allValuesRange;
      const params = {
        range: {
          start: start.format('YYYY-MM-DD HH:mm:ss'),
          end: end.format('YYYY-MM-DD HH:mm:ss'),
        },
        teams: this.selectedTeamIds,
      };
      if (this.mgtIdcOptViewMode === 1) {
        params.libraryIds = this.mgtIdcOptSelectedLibraryIndicators;
        this.$store.dispatch(FETCH_CUMULATED_INDICATORS, params).then((data) => {
          this.accuThemes = data.themes;
          this.cumulationTeamsData = data.teams;
          this.setupDetailedIndic();
          this.redrawAll();
        });
      } else {
        this.$store.dispatch(FETCH_MANAGED_INDICATORS, params).then((data) => {
          this.teamsData = data;
        });
      }
    },
    /** Same as above but may need to be execute after refreshing */
    setupDetailedIndic() {
      const indicId = Number(this.$route.query.detailed);
      for (let i = 0, ilen = this.accuThemes.length, theme; i < ilen; i++) {
        theme = this.accuThemes[i];
        for (let j = 0, jlen = theme.indicators.length; j < jlen; j++) {
          if (theme.indicators[j].id == this.detailedIndicId) {
            this.showDetailedIndic(theme.indicators[j]);
            return;
          }
        }
      }
      this.detailedIndicDisplayed = false;
    },
    redrawAll() {
      if (this.detailedIndicDisplayed && this.$refs.indicatorDetailed) {
        this.$refs.indicatorDetailed.makeCharts(true);
        this.$refs.indicatorDetailed.placeRange();
      }
    },
    /** Details of a specific indicator */
    showDetailedIndic(indic) {
      // Fetch all the values
      const selTeams = this.selectedTeamIds;
      const cRange = { cStart: this.$moment().subtract(200, 'days'), cEnd: this.$moment() };
      cRange.start = cRange.cStart.format('YYYY-MM-DD HH:mm:ss');
      cRange.end = cRange.cEnd.format('YYYY-MM-DD HH:mm:ss');
      const params = { teams: selTeams, range: cRange, libraryIds: [indic.id] };
      this.detailedIndicName = indic.name;
      this.detailedIndicDisplayed = true;
      this.$store.dispatch(FETCH_CUMULATED_INDICATORS, params).then((data) => {
        const comp = this.$refs.indicatorDetailed;
        if (comp) {
          indic = data.themes[0].indicators[0];
          comp.indicator = indic;
          comp.fetchProblemsActions = function () {};
        }
      });
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'mgtIdcOptDefaultConfig') {
        this.mgtIdcOptDefaultConfig = option.value;
        return;
      } else if (option.key === 'teams') {
        query.mgtTeams = option.value.join(',');
      } else if (option.key === 'mgtIdcDisplayGw') {
        query.mgtIdcDisplayGw = option.value ? 'true' : 'false';
      } else if (option.key === 'viewMode') {
        query.viewMode = Number(option.value) === 1 ? 1 : 0;
      } else if (option.key === 'mgtIdcOptSelectedLibraryIndicators') {
        query.mgtIdcOptLibraryIndicators = option.value.join(',');
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.mgt-indicators {
  position: relative;
  overflow: hidden;

  &__templates {
    display: flex;
    align-items: center;
    margin-left: 1rem;

    &.editable-label {
      .legend.legend--bottom-margin {
        margin-bottom: 0px;
        margin-right: .3rem;
        min-height: 1.3rem;
        display: flex;
        align-items: center;
      }

      .editable-label-text {
        display: flex;
        align-items: center;
      }
    }
  }

  .f-h-below-filters {
    position: relative;

    > .indicators, > .mgt-indic-details {
      z-index: 10001;
      position: absolute;
      top: 0px;
      left: 0px;
    }
    .mgt-indic-details {
      background: #ffffff;

      h2 {
        line-height: 2em;
        height: 2em;
        font-size: 1.25em;
        margin: 0px;
        text-align: center;
        font-weight: 600;
      }
    }
  }
  .below-h2 {
    height: calc(100% - 2.5em);
  }

  .management-indicators-compact-row:last-child {
    margin-bottom: 20px;
  }
  .advanced-team-picker .wrapper {
    display: block;

    .legend, .text {
      display: inline;
      white-space: normal;
    }
    .legend {
      float: left;
    }
  }
}

@media (max-width: $tablet-landscape) {
  .mgt-indicators {
    &__templates {
      display: none;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .mgt-indicators {
    &__view {
      display: none;
    }
  }
}
</style>
